import { read, utils } from 'xlsx';

/**
 * Converts an Excel file (.xlsx, .xls) to CSV format
 * @param file - The Excel file to convert (must have exactly 1 sheet)
 * @returns Promise<File> - A new File object containing CSV data
 */
export const convertExcelToCSV = async (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          reject(new Error('Failed to read file'));
          return;
        }

        // Parse the Excel file
        const workbook = read(data, { type: 'binary' });

        // Get the first sheet (validation ensures only 1 sheet exists)
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const rawData: any[][] = utils.sheet_to_json(worksheet, { header: 1 });

        const filteredData = rawData.filter((row, index) => {
          if (index === 0) return true;
          return row.some(cell =>
            cell !== null && cell !== undefined && String(cell).trim() !== ''
          );
        });

        const filteredWorksheet = utils.aoa_to_sheet(filteredData);
        const csv = utils.sheet_to_csv(filteredWorksheet);

        // Create a new File object with CSV content
        const csvBlob = new Blob([csv], { type: 'text/csv' });
        const csvFile = new File(
          [csvBlob],
          file.name.replace(/\.(xlsx|xls)$/i, '.csv'),
          {
            type: 'text/csv',
          }
        );

        resolve(csvFile);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsBinaryString(file);
  });
};

/**
 * Checks if a file is an Excel file
 * @param file - The file to check
 * @returns boolean - True if file is .xlsx or .xls
 */
export const isExcelFile = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  return fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
};
